import React, { useState, useEffect } from 'react';
import { Bot, User, X, AlertCircle, RefreshCw } from 'lucide-react';
import { apiService } from '../../services/api';

const useSessionManager = ({ phone, onStatusChange }) => {
    const [sessionStatus, setSessionStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchSessionStatus = async () => {
        if (!phone) return;

        try {
            setLoading(true);
            setError(null);
            const response = await apiService.getSessionStatus(phone);
            console.log('Session status response:', response);

            // Handle case where session is in a nested property
            const status = response.session || response;
            setSessionStatus(status);
            if (onStatusChange) onStatusChange(status);
        } catch (err) {
            console.error('Error fetching session status:', err);
            setError('Failed to fetch session status');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSessionStatus();
    }, [phone]);

    const handleTakeover = async () => {
        try {
            setLoading(true);
            setError(null);
            await apiService.takeoverSession(phone);
            await fetchSessionStatus();
        } catch (err) {
            console.error('Error taking over session:', err);
            setError('Failed to take over session');
        } finally {
            setLoading(false);
        }
    };

    const handleReturnToBot = async () => {
        try {
            setLoading(true);
            setError(null);
            await apiService.returnToBot(phone);
            await fetchSessionStatus();
        } catch (err) {
            console.error('Error returning to bot:', err);
            setError('Failed to return to bot');
        } finally {
            setLoading(false);
        }
    };

    const handleEndSession = async () => {
        try {
            setLoading(true);
            setError(null);
            await apiService.endSession(phone);
            await fetchSessionStatus();
        } catch (err) {
            console.error('Error ending session:', err);
            setError('Failed to end session');
        } finally {
            setLoading(false);
        }
    };

    const getStatusLabel = () => {
        if (!sessionStatus) return 'Unknown';

        if (sessionStatus.mode === 'bot') {
            return 'Bot Mode';
        } else if (sessionStatus.mode === 'agent' || sessionStatus.mode === 'human') {
            return 'Agent Mode';
        } else if (sessionStatus.mode === 'queue' || sessionStatus.inHumanQueue) {
            return 'In Queue';
        } else {
            return sessionStatus.mode || 'Unknown';
        }
    };

    const getStatusIcon = () => {
        if (!sessionStatus) return <AlertCircle size={16} />;

        if (sessionStatus.mode === 'bot') {
            return <Bot size={16} />;
        } else if (sessionStatus.mode === 'agent' || sessionStatus.mode === 'human') {
            return <User size={16} />;
        } else if (sessionStatus.mode === 'queue' || sessionStatus.inHumanQueue) {
            return <RefreshCw size={16} />;
        } else {
            return <AlertCircle size={16} />;
        }
    };

    return {
        status: sessionStatus,
        statusLabel: getStatusLabel(),
        statusIcon: getStatusIcon(),
        isLoading: loading,
        error,
        takeover: handleTakeover,
        returnToBot: handleReturnToBot,
        endSession: handleEndSession,
        refresh: fetchSessionStatus
    };
};

export default useSessionManager; 